import React, { useState, useEffect } from 'react';
import { Plus, Upload } from 'lucide-react';
import { usePageConfig } from '../../contexts/PageConfigContext';
import { farmersApi, FarmerDto, CreateFarmerDto, UpdateFarmerDto } from '../../services/api';
import { FarmersList } from './components/FarmersList';
import { FarmerForm } from './components/FarmerForm';
import { Dialog } from '../../components/Dialog';
import { usePermission } from '../../hooks/usePermission';
import { Permissions } from '../../constants/permissions';
import { BulkImportDialog } from '../../components/BulkImportDialog';

export const FarmersPage: React.FC = () => {
  const { setPageConfig } = usePageConfig();
  const [farmers, setFarmers] = useState<FarmerDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [selectedFarmer, setSelectedFarmer] = useState<FarmerDto | undefined>();
  const [isSaving, setIsSaving] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);

  // Permission checks
  const canCreate = usePermission(Permissions.Farmers.Create);
  const canEdit = usePermission(Permissions.Farmers.Edit);
  const canDelete = usePermission(Permissions.Farmers.Delete);

  useEffect(() => {
    setPageConfig({
      title: 'Agricultores',
      description: 'Gestión de agricultores y proveedores',
      actions: canCreate ? [
        {
          label: 'Importar',
          icon: Upload,
          onClick: () => setShowImportDialog(true),
          variant: 'secondary' as const,
          iconOnly: true,
          tooltip: 'Importar desde Excel'
        },
        {
          label: 'Nuevo Agricultor',
          icon: Plus,
          onClick: () => {
            setSelectedFarmer(undefined);
            setShowFormDialog(true);
          },
          variant: 'primary',
        },
      ] : [],
    });

    loadFarmers();
  }, [setPageConfig, canCreate]);

  const loadFarmers = async () => {
    try {
      setIsLoading(true);
      const data = await farmersApi.getAll();
      setFarmers(data);
    } catch (error) {
      console.error('Error loading farmers:', error);
      alert('Error al cargar los agricultores');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (data: CreateFarmerDto | UpdateFarmerDto) => {
    try {
      setIsSaving(true);
      if (selectedFarmer) {
        await farmersApi.update(selectedFarmer.id, data as UpdateFarmerDto);
      } else {
        await farmersApi.create(data as CreateFarmerDto);
      }
      await loadFarmers();
      setShowFormDialog(false);
      setSelectedFarmer(undefined);
    } catch (error: any) {
      console.error('Error saving farmer:', error);
      alert(error.message || 'Error al guardar el agricultor');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = async (id: string) => {
    try {
      const farmer = await farmersApi.getById(id);
      setSelectedFarmer(farmer);
      setShowFormDialog(true);
    } catch (error) {
      console.error('Error loading farmer:', error);
      alert('Error al cargar el agricultor');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este agricultor?')) {
      return;
    }

    try {
      await farmersApi.delete(id);
      await loadFarmers();
    } catch (error: any) {
      console.error('Error deleting farmer:', error);
      alert(error.message || 'Error al eliminar el agricultor');
    }
  };

  return (
    <div className="w-full">
      <FarmersList
        farmers={farmers}
        isLoading={isLoading}
        onEdit={canEdit ? handleEdit : undefined}
        onDelete={canDelete ? handleDelete : undefined}
      />

      <Dialog
        isOpen={showFormDialog}
        onClose={() => {
          setShowFormDialog(false);
          setSelectedFarmer(undefined);
        }}
        title={selectedFarmer ? 'Editar Agricultor' : 'Nuevo Agricultor'}
        size="md"
      >
        <FarmerForm
          farmer={selectedFarmer}
          onSave={handleSave}
          onCancel={() => {
            setShowFormDialog(false);
            setSelectedFarmer(undefined);
          }}
          isLoading={isSaving}
        />
      </Dialog>

      <BulkImportDialog
        isOpen={showImportDialog}
        onClose={() => setShowImportDialog(false)}
        entityType="farmers"
        fields={[
          { name: 'name', label: 'Nombre', required: true },
          { name: 'externalId', label: 'ID Externo', required: false },
          { name: 'isActive', label: 'Activo', required: false }
        ]}
        onImport={farmersApi.bulkImport}
      />
    </div>
  );
};
